import * as React from 'react';
import classnames from 'classnames';

import SVGIcon from 'components/svg-icon/SVGIcon';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Spinner from 'components/spinner/Spinner';
import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';

import ImageTileWrapper from './ImageTileWrapper';

import { __t, applyTranslations } from 'utils/language';

import type { ProposalImage } from 'types';
import type { ProposalTileChannelType } from 'modules/proposal/types/ProposalTile.type';

applyTranslations(require('./image-tile.i18n.json'));

type Props = {
  hasConsent: boolean,
  channelType: ProposalTileChannelType,
  index: number,
  canSetMedia?: boolean,
  withVideoIcon?: boolean,
  post: ProposalImage | null,
  onClick?: () => void,
  onRemove: () => void
};

const ImageTile = (
  {
    hasConsent,
    channelType,
    index,
    post,
    onClick,
    onRemove,
    canSetMedia,
    withVideoIcon
  }: Props
) => {
  const [isHovered, onHover] = React.useState(false);
  const [isFailedToLoad, setFailedToLoad] = React.useState(false);
  const style: React.CSSProperties = {};
  const hasPost = Boolean(post && post.url);

  const addButtonClassNames = classnames(
    'proposal-image-grid-v2__controls', {
      'proposal-image-grid-v2__controls--shown': !hasPost || isHovered
    });

  const removeButtonClassNames = classnames(
    'proposal-image-grid-v2__remove-button',
    'proposal-image-grid-v2__controls', {
      'proposal-image-grid-v2__controls--shown': isHovered
    });

  return (
    <ImageTileWrapper
      hasConsent={hasConsent}
      hasPost={hasPost}
      postUrl={post?.url || undefined}
      className={`proposal-image-grid-v2__block proposal-image-grid-v2__block${index + 1}`}
      style={style}
      onMouseMove={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onClick={onClick}
    >
      {post?.isLoading ? (
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      ) : (
        <React.Fragment>
          {(hasConsent && hasPost && !isFailedToLoad) && (
            <img
              className="proposal-image-grid-v2__image"
              src={post?.assetUrl}
              onError={() => setFailedToLoad(true)}
              alt=""
            />
          )}
          {(hasConsent && hasPost && isFailedToLoad) && (
            <div className="proposal-image-grid-v2__failed-image">
              {__t('image_tile.missing_post')}
            </div>
          )}

          {(!hasConsent && hasPost) && (
            <div className="proposal-image-grid-v2__consents">
              <ConsentPlaceholder
                type={channelType}
                withLearnMore
                withLinkOnTheLeft={canSetMedia}
                link={post?.url ?? ''}
              />
            </div>
          )}

          {(hasConsent && withVideoIcon && hasPost && !canSetMedia && !isFailedToLoad) && (
            <div className="proposal-image-grid-v2__play-button">
              <SVGIcon
                name="play-button"
                width={40}
                height={40}
              />
            </div>
          )}

          {canSetMedia && (
            /** Plus sign icon to add new media */
            <div className={addButtonClassNames}>
              <SVGDynamicIcon
                name="plus-inside-circle-custom-bg-white-fg"
                width={24}
                height={24}
                customColor={isHovered ? 'black' : 'grey-light'}
              />
            </div>
          )}

          {canSetMedia && hasPost && (
            /** Remove post icon */
            <div className={removeButtonClassNames}>
              <SVGDynamicIcon
                name="cross-inside-circle-custom-bg-white-fg"
                width={16}
                height={16}
                customColor="black"
                onClick={event => {
                  event.stopPropagation();
                  event.preventDefault();
                  onRemove();
                }}
              />
            </div>
          )}
        </React.Fragment>
      )}
    </ImageTileWrapper>
  );
};

export default ImageTile;
